import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Users, Bot } from "lucide-react";
import { VbButton, VbPageHeader, VbLoading, VbEmpty, VbError, Callout } from "@/components/vb";
import { useBuyers, useCompanyBuyerCap } from "@/hooks/vb/useBuyers";
import { useWorkspace } from "@/context/WorkspaceContext";
import { BuyerListTable } from "./BuyerListTable";

/** VB-A · Buyer list (overview). Lists every buyer for the active company with
 *  provenance, latest-run status, and VB-METRICS (signals / visibility / SoV /
 *  trend). Row → detail (VB-C); "New buyer" → VB-B1. */
export function BuyerOverviewPage() {
  const navigate = useNavigate();
  // The buyers list is workspace-scoped server-side (X-Workspace-ID header);
  // an explicit `?company=<id>` narrows it within the workspace. Absent →
  // all of the workspace's buyers. "New buyer" needs a concrete company, so
  // it falls back to the active workspace's first company (the sidebar lists
  // them in the same order) when no param is present.
  const [searchParams] = useSearchParams();
  const { activeWorkspace } = useWorkspace();
  const companyParam = searchParams.get("company");
  const companyId = companyParam && /^\d+$/.test(companyParam) ? Number(companyParam) : undefined;
  const newBuyerCompanyId = companyId ?? activeWorkspace?.companies?.[0]?.company_id;
  // `isPending` (not `isLoading`) so the not-yet-resolved state - including the
  // brief auth-gated/disabled window on a cold load - shows the skeleton rather
  // than falling through to the empty state.
  const { data: buyers, isPending, isError, refetch } = useBuyers(companyId);

  // Buyers are capped per company, not per workspace (AITS-417), so the gate
  // follows the company "New buyer" would create IN - not the list on screen.
  // With `?company=` present the two are the same query and this is free;
  // without it, the page lists the workspace while the button targets the
  // fallback company, and only the latter decides whether it's offered.
  const cap = useCompanyBuyerCap(newBuyerCompanyId);
  const newBuyerCompanyName = activeWorkspace?.companies?.find(
    (c) => c.company_id === newBuyerCompanyId,
  )?.company_name;

  const newBuyer = (
    <VbButton
      className="vb-btn--block-mobile"
      onClick={() => navigate(`/virtual-buyer/new?company=${newBuyerCompanyId}`)}
      disabled={!newBuyerCompanyId || cap.atCap}
    >
      <Plus className="h-4 w-4" />
      New buyer
    </VbButton>
  );

  return (
    <>
      <VbPageHeader
        title="Virtual Buyers"
        subtitle="The personas we ask AI about your business, and how each one sees you."
        actions={newBuyer}
      />

      {/* Why the button above is dead. Named per company, since the same
          workspace can add a buyer to any company that still has room.
          `neutral`, not `info`: the brand tint is what the MarcusAI tip below
          uses, and the two read as one block. */}
      {cap.atCap && (
        <Callout className="mb-5" tone="neutral" role="status" icon={<Users size={15} />}>
          <strong>{newBuyerCompanyName || "This company"}</strong>{" "}
          {cap.used > cap.max ? (
            <>has {cap.used} virtual buyers, more than the {cap.max} we allow for one company.</>
          ) : (
            <>has all {cap.max} virtual buyers, the most we allow for one company.</>
          )}{" "}
          {/* `mustDelete`, not "one": above the cap, deleting one still leaves
              them over it and the button stays dead. */}
          Delete {cap.mustDelete === 1 ? "one" : cap.mustDelete} to make room, or pick another
          company in the sidebar to add a buyer there.
        </Callout>
      )}

      {isError ? (
        <VbError description="We couldn't load your buyers." onRetry={() => refetch()} />
      ) : isPending ? (
        <VbLoading rows={4} />
      ) : !buyers || buyers.length === 0 ? (
        <VbEmpty
          icon={<Users className="h-5 w-5" />}
          title="No virtual buyers yet"
          description="Create a buyer persona and we'll ask AI the questions they would, then show you how you measure up."
          action={newBuyer}
        />
      ) : (
        <>
          {/* MarcusAI tip - static cosmetic (per-buyer guidance lands with the
              Workbench capability). Generic copy, no fabricated buyer data. */}
          <div className="vb-marcus">
            <div className="vb-marcus__icon">
              <Bot className="h-4 w-4" />
            </div>
            <div className="vb-marcus__body">
              <span className="vb-eyebrow">MarcusAI</span>
              <div className="vb-marcus__quote">
                Each buyer asks AI the questions a real prospect would. Open one to see
                where you show up, which sources AI cites, and the AI Trust Signals you expose.
              </div>
            </div>
          </div>

          <div className="vb-panel">
            <div className="vb-panel-hd">
              <h2>All buyers</h2>
            </div>
            <BuyerListTable buyers={buyers} />
          </div>
        </>
      )}
    </>
  );
}

export default BuyerOverviewPage;
